import { useEffect, useState, useCallback } from "react";
import { BarChart3, Users, HeartPulse, MapPin, FileWarning, FileText, FileSpreadsheet, Download, Lock, Unlock, ArrowLeft, ChevronRight, CheckCircle2, AlertTriangle } from "lucide-react";
import { PageLoading, ErrorState } from "@/components/shared";
import api, { API } from "@/lib/api";
import { toast } from "sonner";

const MONTHS = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];
const hm = (s) => `${Math.floor((s || 0) / 3600)}h ${Math.floor(((s || 0) % 3600) / 60).toString().padStart(2, "0")}m`;
const km = (m) => `${((m || 0) / 1000).toLocaleString("de-DE", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} km`;
const STATUS_C = { OPEN: "bg-slate-100 text-slate-500", READY: "bg-emerald-100 text-emerald-700", REVIEW_REQUIRED: "bg-amber-100 text-amber-700", INCOMPLETE: "bg-red-100 text-red-700", FINALIZED: "bg-navy text-white" };

const now = new Date();

async function download(url, filename) {
  const token = localStorage.getItem("eg_token");
  const r = await fetch(`${API}${url}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!r.ok) { toast.error("Export fehlgeschlagen"); return; }
  const blob = await r.blob();
  const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = filename; a.click();
}

const Card = ({ label, value, tone = "navy", testid }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={testid}>
    <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{label}</div>
    <div className={`mt-1 font-heading text-2xl font-extrabold text-${tone}`}>{value}</div>
  </div>
);

const ExportRow = ({ base, name }) => (
  <div className="flex flex-wrap gap-2" data-testid="report-export-row">
    <button onClick={() => download(`${base}.pdf`, `${name}.pdf`)} data-testid="export-pdf" className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-navy hover:bg-slate-50"><FileText className="h-3.5 w-3.5" /> PDF</button>
    <button onClick={() => download(`${base}.xlsx`, `${name}.xlsx`)} data-testid="export-xlsx" className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-navy hover:bg-slate-50"><FileSpreadsheet className="h-3.5 w-3.5" /> Excel</button>
    <button onClick={() => download(`${base}.csv`, `${name}.csv`)} data-testid="export-csv" className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-navy hover:bg-slate-50"><Download className="h-3.5 w-3.5" /> CSV</button>
  </div>
);

const StatusBadge = ({ status, statuses }) => (
  <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_C[status] || "bg-slate-100 text-slate-500"}`}>{(statuses || {})[status] || status}</span>
);

export default function CareReports() {
  const [tab, setTab] = useState("company");
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [selWorker, setSelWorker] = useState(null);
  const [selPatient, setSelPatient] = useState(null);

  const q = `year=${year}&month=${month}`;
  const resetDrill = () => { setSelWorker(null); setSelPatient(null); };

  return (
    <div data-testid="care-reports">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2"><BarChart3 className="h-6 w-6 text-cyan-dark" /><h1 className="font-heading text-2xl font-extrabold text-navy">Pflege Berichte</h1></div>
        <div className="flex items-center gap-2">
          <select value={month} onChange={(e) => { setMonth(+e.target.value); resetDrill(); }} data-testid="rep-month" className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-navy">
            {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
          <select value={year} onChange={(e) => { setYear(+e.target.value); resetDrill(); }} data-testid="rep-year" className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-navy">
            {[now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2].map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      <div className="mb-5 flex flex-wrap gap-1 border-b border-slate-200">
        {[["company", "Unternehmen", Users], ["workers", "Mitarbeiter", Users], ["patients", "Patienten", HeartPulse], ["tours", "Touren", MapPin], ["review", "Prüfungen / Abweichungen", FileWarning]].map(([k, label, Icon]) => (
          <button key={k} onClick={() => { setTab(k); resetDrill(); }} data-testid={`rep-tab-${k}`}
            className={`inline-flex items-center gap-1.5 border-b-2 px-3 py-2.5 text-sm font-semibold transition-colors ${tab === k ? "border-cyan-dark text-cyan-dark" : "border-transparent text-slate-500 hover:text-navy"}`}>
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>

      {(tab === "company") && !selWorker && <CompanyTab q={q} year={year} month={month} onWorker={(id) => { setTab("workers"); setSelWorker(id); }} />}
      {(tab === "workers") && (selWorker ? <WorkerView worker={selWorker} q={q} year={year} month={month} onBack={() => setSelWorker(null)} /> : <WorkerPicker q={q} onPick={setSelWorker} />)}
      {(tab === "patients") && (selPatient ? <PatientView patient={selPatient} q={q} onBack={() => setSelPatient(null)} /> : <PatientsTab q={q} onPick={setSelPatient} />)}
      {(tab === "tours") && <ToursTab q={q} />}
      {(tab === "review") && <ReviewTab q={q} />}
    </div>
  );
}

// ---------------------------------------------------------------- Company
function CompanyTab({ q, year, month, onWorker }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { setD(null); api.get(`/care/reports/company?${q}`).then((r) => setD(r.data)).catch(() => setD({ error: true })); }, [q]);
  useEffect(load, [load]);
  if (!d) return <PageLoading />;
  if (d.error) return <ErrorState title="Pflege Modul nicht aktiv" subtitle="Aktivieren Sie das Pflege Modul oder Pflege Komplett, um Berichte zu sehen." testid="care-reports-locked" />;
  const t = d.totals; const fin = d.finalization;

  const finalize = async () => {
    if (!window.confirm(`Monat ${d.period_label} für das gesamte Unternehmen abschließen?`)) return;
    setBusy(true);
    try { const r = await api.post("/care/reports/finalize", { level: "ORGANIZATION_MONTH", year, month }); toast.success(`Abgeschlossen · Version ${r.data.version}`); load(); }
    catch (e) { const det = e?.response?.data?.detail; toast.error(typeof det === "object" ? `Blockiert: ${det.blockers?.length} offene Punkte` : "Abschluss blockiert"); }
    finally { setBusy(false); }
  };
  const reopen = async () => {
    const reason = window.prompt("Grund für Wiederöffnung:"); if (!reason) return;
    setBusy(true);
    try { await api.post("/care/reports/reopen", { level: "ORGANIZATION_MONTH", year, month, reason }); toast.success("Monat wiedereröffnet"); load(); }
    catch { toast.error("Wiederöffnung fehlgeschlagen"); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="care-company-report">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-heading text-xl font-extrabold text-navy">Unternehmensbericht · {d.period_label}</h2>
        <div className="flex items-center gap-2">
          <ExportRow base={`/care/reports/company?${q}`} name={`unternehmensbericht_${d.period}`} />
          {fin && fin.status === "FINALIZED"
            ? <button onClick={reopen} disabled={busy} data-testid="reopen-org" className="inline-flex items-center gap-1.5 rounded-full bg-amber-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-600 disabled:opacity-50"><Unlock className="h-3.5 w-3.5" /> Wiederöffnen (V{fin.version})</button>
            : <button onClick={finalize} disabled={busy} data-testid="finalize-org" className="inline-flex items-center gap-1.5 rounded-full bg-navy px-3 py-1.5 text-xs font-semibold text-white hover:bg-navy/90 disabled:opacity-50"><Lock className="h-3.5 w-3.5" /> Monat abschließen</button>}
        </div>
      </div>
      {fin && (
        <div className={`flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm ${fin.status === "FINALIZED" ? "border-navy/20 bg-navy/5 text-navy" : "border-amber-200 bg-amber-50 text-amber-700"}`} data-testid="fin-banner">
          {fin.status === "FINALIZED" ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
          <span>Version {fin.version} · {fin.status === "FINALIZED" ? `Abgeschlossen am ${new Date(fin.finalized_at).toLocaleDateString("de-DE")} von ${fin.finalized_by_name}` : `Wiedereröffnet · Grund: ${fin.reopen_reason}`}</span>
          <span className="ml-auto font-mono text-[11px] text-slate-400">calc v{fin.calculation_version}</span>
        </div>
      )}
      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-4">
        <Card label="Gesamtarbeitszeit" value={hm(t.gross)} testid="cm-gross" />
        <Card label="Nettoarbeitszeit" value={hm(t.net)} testid="cm-net" />
        <Card label="Besuchszeit" value={hm(t.visit)} tone="cyan-dark" testid="cm-visit" />
        <Card label="Fahr-/Wegezeit" value={hm(t.travel)} tone="amber-600" testid="cm-travel" />
        <Card label="Pause" value={hm(t.break)} testid="cm-break" />
        <Card label="Sonstige Arbeitszeit" value={hm(t.other)} testid="cm-other" />
        <Card label="Kilometer" value={km(t.distance_m)} tone="cyan-dark" testid="cm-km" />
        <Card label="Besuche" value={t.visit_count} testid="cm-visits" />
        <Card label="Aktive Mitarbeiter" value={d.active_workers} testid="cm-workers" />
        <Card label="Aktive Patienten" value={d.active_patients} testid="cm-patients" />
        <Card label="Offene Prüfungen" value={d.open_reviews} tone={d.open_reviews ? "amber-600" : "emerald-600"} testid="cm-reviews" />
        <Card label="KM (Route/Manuell)" value={`${((d.km.route) / 1000).toFixed(1)} / ${((d.km.manual) / 1000).toFixed(1)}`} testid="cm-km-src" />
      </div>
      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-left text-sm" data-testid="cm-worker-table">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-400">
            <tr>{["Mitarbeiter", "Worker ID", "Brutto", "Pause", "Netto", "Besuch", "Fahrt", "Sonstige", "Besuche", "KM", "Status"].map((h) => <th key={h} className="px-3 py-2.5 font-semibold">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {d.workers.map((w) => (
              <tr key={w.employee_id} onClick={() => onWorker(w.employee_id)} className="cursor-pointer hover:bg-slate-50" data-testid={`cm-worker-${w.employee_id}`}>
                <td className="px-3 py-2.5 font-semibold text-navy">{w.name}</td>
                <td className="px-3 py-2.5 font-mono text-xs text-slate-500">{w.worker_id}</td>
                <td className="px-3 py-2.5">{hm(w.totals.gross)}</td>
                <td className="px-3 py-2.5 text-slate-500">{hm(w.totals.break)}</td>
                <td className="px-3 py-2.5 font-semibold">{hm(w.totals.net)}</td>
                <td className="px-3 py-2.5 text-cyan-dark">{hm(w.totals.visit)}</td>
                <td className="px-3 py-2.5 text-amber-600">{hm(w.totals.travel)}</td>
                <td className="px-3 py-2.5 text-slate-500">{hm(w.totals.other)}</td>
                <td className="px-3 py-2.5">{w.totals.visit_count}</td>
                <td className="px-3 py-2.5">{km(w.totals.distance_m)}</td>
                <td className="px-3 py-2.5"><StatusBadge status={w.status} statuses={d.month_statuses} /></td>
              </tr>
            ))}
            {d.workers.length === 0 && <tr><td colSpan={11} className="px-3 py-8 text-center text-slate-400">Keine Mitarbeiteraktivität im Zeitraum.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Worker
function WorkerPicker({ q, onPick }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/care/reports/company?${q}`).then((r) => setD(r.data)).catch(() => setD({ workers: [] })); }, [q]);
  if (!d) return <PageLoading />;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-2" data-testid="worker-picker">
      {d.workers.map((w) => (
        <button key={w.employee_id} onClick={() => onPick(w.employee_id)} data-testid={`wp-${w.employee_id}`} className="flex w-full items-center justify-between rounded-xl px-4 py-3 text-left hover:bg-slate-50">
          <div><div className="text-sm font-semibold text-navy">{w.name}</div><div className="font-mono text-xs text-slate-400">{w.worker_id}</div></div>
          <div className="flex items-center gap-3"><StatusBadge status={w.status} statuses={d.month_statuses} /><ChevronRight className="h-4 w-4 text-slate-300" /></div>
        </button>
      ))}
      {d.workers.length === 0 && <div className="p-8 text-center text-slate-400">Keine Mitarbeiter im Zeitraum.</div>}
    </div>
  );
}

function WorkerView({ worker, q, year, month, onBack }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { setD(null); api.get(`/care/reports/worker?worker=${worker}&${q}`).then((r) => setD(r.data)).catch(() => setD({ error: true })); }, [worker, q]);
  useEffect(load, [load]);
  if (!d) return <PageLoading />;
  if (d.error) return <ErrorState title="Bericht nicht verfügbar" subtitle="Für diese Auswahl liegen keine Daten vor." />;
  const t = d.totals; const fin = d.finalization;

  const finalize = async () => {
    setBusy(true);
    try { const r = await api.post("/care/reports/finalize", { level: "WORKER_MONTH", worker, year, month }); toast.success(`Abgeschlossen · V${r.data.version}`); load(); }
    catch (e) { const det = e?.response?.data?.detail; toast.error(typeof det === "object" ? `Blockiert: ${det.blockers?.length} offene Punkte` : "Abschluss blockiert"); }
    finally { setBusy(false); }
  };
  const reopen = async () => {
    const reason = window.prompt("Grund für Wiederöffnung:"); if (!reason) return;
    setBusy(true);
    try { await api.post("/care/reports/reopen", { level: "WORKER_MONTH", worker, year, month, reason }); toast.success("Wiedereröffnet"); load(); }
    catch { toast.error("Fehlgeschlagen"); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="care-worker-report">
      <button onClick={onBack} data-testid="worker-back" className="inline-flex items-center gap-1 text-sm font-semibold text-cyan-dark hover:underline"><ArrowLeft className="h-4 w-4" /> Zurück</button>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h2 className="font-heading text-xl font-extrabold text-navy">{d.name}</h2><div className="mt-0.5 font-mono text-xs text-slate-400">{d.worker_id} · {d.care_role_label} · {d.period_label}</div></div>
        <div className="flex items-center gap-2">
          <ExportRow base={`/care/reports/worker?worker=${worker}&${q}`} name={`mitarbeiterbericht_${d.worker_id}_${d.period}`} />
          {fin && fin.status === "FINALIZED"
            ? <button onClick={reopen} disabled={busy} data-testid="reopen-worker" className="inline-flex items-center gap-1.5 rounded-full bg-amber-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-600 disabled:opacity-50"><Unlock className="h-3.5 w-3.5" /> Wiederöffnen (V{fin.version})</button>
            : <button onClick={finalize} disabled={busy} data-testid="finalize-worker" className="inline-flex items-center gap-1.5 rounded-full bg-navy px-3 py-1.5 text-xs font-semibold text-white hover:bg-navy/90 disabled:opacity-50"><Lock className="h-3.5 w-3.5" /> Monat abschließen</button>}
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <Card label="Brutto" value={hm(t.gross)} testid="wm-gross" />
        <Card label="Pause" value={hm(t.break)} testid="wm-break" />
        <Card label="Netto" value={hm(t.net)} testid="wm-net" />
        <Card label="Besuchszeit" value={hm(t.visit)} tone="cyan-dark" testid="wm-visit" />
        <Card label="Fahr-/Wegezeit" value={hm(t.travel)} tone="amber-600" testid="wm-travel" />
        <Card label="Sonstige" value={hm(t.other)} testid="wm-other" />
        <Card label="Kilometer" value={km(t.distance_m)} tone="cyan-dark" testid="wm-km" />
        <Card label="Besuche" value={t.visit_count} testid="wm-visits" />
        <Card label="Arbeitstage" value={t.working_days} testid="wm-days" />
        <Card label="Status" value={<StatusBadge status={d.status} statuses={d.month_statuses} />} testid="wm-status" />
      </div>
      <div>
        <h3 className="mb-2 font-heading text-lg font-extrabold text-navy">Tagesübersicht</h3>
        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
          <table className="w-full text-left text-sm" data-testid="wm-day-table">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-400"><tr>{["Datum", "Ein", "Aus", "Brutto", "Pause", "Netto", "Besuch", "Fahrt", "Sonstige", "Bes.", "KM", "Status"].map((h) => <th key={h} className="px-3 py-2 font-semibold">{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-slate-100">
              {d.days.map((r) => (
                <tr key={r.date} data-testid={`wm-day-${r.date}`}>
                  <td className="px-3 py-2 font-mono text-xs">{r.date.slice(5)}</td>
                  <td className="px-3 py-2 text-slate-500">{r.clock_in || "—"}</td><td className="px-3 py-2 text-slate-500">{r.clock_out || "—"}</td>
                  <td className="px-3 py-2">{hm(r.gross)}</td><td className="px-3 py-2 text-slate-500">{hm(r.break)}</td><td className="px-3 py-2 font-semibold">{hm(r.net)}</td>
                  <td className="px-3 py-2 text-cyan-dark">{hm(r.visit)}</td><td className="px-3 py-2 text-amber-600">{hm(r.travel)}</td><td className="px-3 py-2 text-slate-500">{hm(r.other)}</td>
                  <td className="px-3 py-2">{r.visit_count}</td><td className="px-3 py-2">{km(r.distance_m)}</td><td className="px-3 py-2"><StatusBadge status={r.status} statuses={d.month_statuses} /></td>
                </tr>
              ))}
              {d.days.length === 0 && <tr><td colSpan={12} className="px-3 py-6 text-center text-slate-400">Keine Tage.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      <div>
        <h3 className="mb-2 font-heading text-lg font-extrabold text-navy">Patienten im Zeitraum</h3>
        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
          <table className="w-full text-left text-sm" data-testid="wm-patient-table">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-400"><tr>{["Patient", "Patient ID", "Besuche", "Besuchszeit", "Ø Dauer"].map((h) => <th key={h} className="px-3 py-2 font-semibold">{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-slate-100">
              {d.patients.map((p, i) => (
                <tr key={i} data-testid={`wm-patient-${i}`}><td className="px-3 py-2 font-semibold text-navy">{p.name}</td><td className="px-3 py-2 font-mono text-xs text-slate-500">{p.patient_number}</td><td className="px-3 py-2">{p.visits}</td><td className="px-3 py-2 text-cyan-dark">{hm(p.visit_seconds)}</td><td className="px-3 py-2 text-slate-500">{hm(p.avg_seconds)}</td></tr>
              ))}
              {d.patients.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-400">Keine Besuche.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Patients
function PatientsTab({ q, onPick }) {
  const [d, setD] = useState(null); const [search, setSearch] = useState("");
  useEffect(() => { api.get(`/care/reports/patients?${q}${search ? `&q=${encodeURIComponent(search)}` : ""}`).then((r) => setD(r.data)).catch((e) => setD({ patients: [], error: e?.response?.status === 403 })); }, [q, search]);
  if (!d) return <PageLoading />;
  if (d.error) return <ErrorState title="Keine Berechtigung" subtitle="Patientenberichte sind nur für berechtigte Pflege-Rollen verfügbar." testid="patients-forbidden" />;
  return (
    <div className="space-y-4" data-testid="care-patients-report">
      <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Patient ID oder Name suchen…" data-testid="patient-search" className="w-full max-w-sm rounded-lg border border-slate-200 px-3 py-2 text-sm" />
      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-left text-sm" data-testid="pt-table">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-400"><tr>{["Patient", "Patient ID", "Pflegegrad", "Besuche", "Besuchszeit", "Ø Dauer", "Mitarbeiter", "Verpasst", "Prüfung"].map((h) => <th key={h} className="px-3 py-2 font-semibold">{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-slate-100">
            {d.patients.map((p) => (
              <tr key={p.patient_id} onClick={() => onPick(p.patient_id)} className="cursor-pointer hover:bg-slate-50" data-testid={`pt-${p.patient_id}`}>
                <td className="px-3 py-2 font-semibold text-navy">{p.name}</td><td className="px-3 py-2 font-mono text-xs text-slate-500">{p.patient_number}</td><td className="px-3 py-2 text-slate-500">{p.care_grade_label}</td>
                <td className="px-3 py-2">{p.visits}</td><td className="px-3 py-2 text-cyan-dark">{hm(p.visit_seconds)}</td><td className="px-3 py-2 text-slate-500">{hm(p.avg_seconds)}</td>
                <td className="px-3 py-2">{p.workers}</td><td className="px-3 py-2">{p.missed || "—"}</td><td className="px-3 py-2">{p.review ? <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">{p.review}</span> : "—"}</td>
              </tr>
            ))}
            {d.patients.length === 0 && <tr><td colSpan={9} className="px-3 py-8 text-center text-slate-400">Keine Patienten im Zeitraum.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PatientView({ patient, q, onBack }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/care/reports/patient?patient=${patient}&${q}`).then((r) => setD(r.data)).catch(() => setD({ error: true })); }, [patient, q]);
  if (!d) return <PageLoading />;
  if (d.error) return <ErrorState title="Bericht nicht verfügbar" subtitle="Für diese Auswahl liegen keine Daten vor." />;
  return (
    <div className="space-y-4" data-testid="care-patient-detail">
      <button onClick={onBack} data-testid="patient-back" className="inline-flex items-center gap-1 text-sm font-semibold text-cyan-dark hover:underline"><ArrowLeft className="h-4 w-4" /> Zurück</button>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h2 className="font-heading text-xl font-extrabold text-navy">{d.patient.name}</h2><div className="mt-0.5 font-mono text-xs text-slate-400">{d.patient.patient_number} · {d.care_grade_label} · {d.period_label}</div></div>
        <ExportRow base={`/care/reports/patient?patient=${patient}&${q}`} name={`patientenbericht_${d.patient.patient_number}_${d.period}`} />
      </div>
      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <Card label="Besuche" value={d.visits} testid="pd-visits" />
        <Card label="Gesamtzeit" value={hm(d.total_visit_seconds)} tone="cyan-dark" testid="pd-total" />
        <Card label="Ø Dauer" value={hm(d.avg_seconds)} testid="pd-avg" />
        <Card label="Mitarbeiter" value={d.workers_involved.length} testid="pd-workers" />
        <Card label="Verpasst/Prüfung" value={`${d.missed} / ${d.review}`} tone="amber-600" testid="pd-issues" />
      </div>
      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-left text-sm" data-testid="pd-table">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-400"><tr>{["Datum", "Visit", "Mitarbeiter", "Geplant", "Ist Start", "Ist Ende", "Dauer", "Status", "Quelle", "Standort", "Tour"].map((h) => <th key={h} className="px-3 py-2 font-semibold">{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-slate-100">
            {d.detail.map((r) => (
              <tr key={r.visit_id} data-testid={`pd-visit-${r.visit_id}`}>
                <td className="px-3 py-2 font-mono text-xs">{r.date.slice(5)}</td><td className="px-3 py-2 font-mono text-[11px] text-slate-500">{r.visit_number}</td><td className="px-3 py-2 text-navy">{r.worker}</td>
                <td className="px-3 py-2 text-slate-500">{r.scheduled_start || "—"}</td><td className="px-3 py-2">{r.actual_start || "—"}</td><td className="px-3 py-2">{r.actual_end || "—"}</td>
                <td className="px-3 py-2 text-cyan-dark">{r.duration_seconds ? hm(r.duration_seconds) : "—"}</td><td className="px-3 py-2"><span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">{r.status_label || r.status}</span></td>
                <td className="px-3 py-2 text-slate-400">{r.source || "—"}</td><td className="px-3 py-2 text-slate-400 text-[11px]">{r.location_verified || "—"}</td><td className="px-3 py-2 text-slate-400 text-[11px]">{r.tour_id ? "✓" : "—"}</td>
              </tr>
            ))}
            {d.detail.length === 0 && <tr><td colSpan={11} className="px-3 py-8 text-center text-slate-400">Keine Besuche.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Tours
function ToursTab({ q }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/care/reports/tours?${q}`).then((r) => setD(r.data)).catch(() => setD({ tours: [] })); }, [q]);
  if (!d) return <PageLoading />;
  return (
    <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white" data-testid="care-tours-report">
      <table className="w-full text-left text-sm" data-testid="tr-table">
        <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-400"><tr>{["Tour", "Datum", "Mitarbeiter", "Geplant", "Erledigt", "Verpasst", "Plan-Zeit", "Ist-Besuch", "Fahrt", "KM", "Status"].map((h) => <th key={h} className="px-3 py-2 font-semibold">{h}</th>)}</tr></thead>
        <tbody className="divide-y divide-slate-100">
          {d.tours.map((t) => (
            <tr key={t.tour_id} data-testid={`tr-${t.tour_id}`}>
              <td className="px-3 py-2 font-mono text-xs text-navy">{t.tour_number}</td><td className="px-3 py-2 font-mono text-xs">{t.date?.slice(5)}</td><td className="px-3 py-2 text-navy">{t.worker}</td>
              <td className="px-3 py-2">{t.planned_visits}</td><td className="px-3 py-2 text-emerald-600">{t.completed_visits}</td><td className="px-3 py-2 text-red-500">{t.missed_visits || "—"}</td>
              <td className="px-3 py-2 text-slate-500">{hm(t.planned_seconds)}</td><td className="px-3 py-2 text-cyan-dark">{hm(t.actual_visit_seconds)}</td><td className="px-3 py-2 text-amber-600">{hm(t.travel_seconds)}</td>
              <td className="px-3 py-2">{km(t.distance_m)}</td><td className="px-3 py-2"><span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">{t.status_label || t.status}</span></td>
            </tr>
          ))}
          {d.tours.length === 0 && <tr><td colSpan={11} className="px-3 py-8 text-center text-slate-400">Keine Touren im Zeitraum.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------- Review center
function ReviewTab({ q }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/care/reports/review?${q}`).then((r) => setD(r.data)).catch(() => setD({ categories: [], total: 0 })); }, [q]);
  if (!d) return <PageLoading />;
  return (
    <div className="space-y-4" data-testid="care-review-center">
      <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="rc-summary">
        <span className="text-sm text-slate-500">Offene Prüfpunkte gesamt: </span>
        <span className={`font-heading text-2xl font-extrabold ${d.total ? "text-amber-600" : "text-emerald-600"}`}>{d.total}</span>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        {d.categories.map((c) => (
          <div key={c.key} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`rc-cat-${c.key}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><FileWarning className={`h-4 w-4 ${c.count ? "text-amber-600" : "text-slate-300"}`} /><span className="text-sm font-semibold text-navy">{c.label}</span></div>
              <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${c.count ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-400"}`}>{c.count}</span>
            </div>
            {c.count > 0 && (
              <div className="mt-3 space-y-1.5">
                {c.items.slice(0, 6).map((it, i) => (
                  <div key={i} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-1.5 text-xs" data-testid={`rc-item-${c.key}-${i}`}>
                    <span className="text-navy">{it.worker || it.worker_id || "—"}</span>
                    <span className="font-mono text-slate-400">{it.date || it.patient_number || ""}</span>
                  </div>
                ))}
                {c.count > 6 && <div className="text-[11px] text-slate-400">+ {c.count - 6} weitere</div>}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
